import { useEffect, useState, type ReactNode } from 'react'
import type { OptionsFilter, SelectProps } from '@mantine/core'
import { ActionIcon, Box, Button, Card, Collapse, Group, ScrollArea, Stack, Text, Tooltip } from '@mantine/core'
import { IconBook, IconChevronDown, IconChevronUp, IconMessage, IconPaperclip } from '@/components/icons'
import { TicketCustomerFieldsCard } from '@/components/tickets/TicketCustomerFieldsCard'
import { TicketCustomerInfoCard } from '@/components/tickets/TicketCustomerInfoCard'
import { TicketManagementCard } from '@/components/tickets/TicketManagementCard'
import { TicketPreviousTicketsCard } from '@/components/tickets/TicketPreviousTicketsCard'
import { TicketReviewsCard } from '@/components/tickets/TicketReviewsCard'
import { TicketSdAdminCard } from '@/components/tickets/TicketSdAdminCard'
import { TicketViewersCard } from '@/components/tickets/TicketViewersCard'
import type { DisplayIdent } from '@/components/tickets/ticket-detail-page-helpers'
import type { PreviousTicketsRowsListProps } from '@/components/tickets/TicketDetailSidePanels'
import type {
  CustomerRow,
  ProjectCustomerFieldRow,
  TicketDetailTicket,
  TicketListItem,
  TicketProductReviewStatusResponse,
  TicketReviewStatusResponse,
  TicketSdAdminApiAction,
  TicketSdAdminApiActionResponse,
  TicketViewerRow,
  WorkspaceUserRow,
} from '@/types/api'

type SelectOption = {
  value: string
  label: string
}

type SdAdminActionItem = {
  key: TicketSdAdminApiAction
  label: string
}

type SdAdminEmailChoice = {
  action: TicketSdAdminApiAction
  emails: string[]
  sendToCustomer?: boolean
}

type TicketSidebarPanelProps = {
  ticket: TicketDetailTicket
  currentUser: { id?: string | null; role?: string | null } | null | undefined
  isPhoneLayout: boolean
  isTicketWideLayout: boolean
  classicBadgeDark: boolean
  statusOptions: SelectOption[]
  assigneeOptions: SelectOption[]
  assigneeUserById: Map<string, WorkspaceUserRow>
  assigneeFilter: OptionsFilter
  renderAssigneeOption: SelectProps['renderOption']
  showMyWorkReview: boolean
  showProductReview: boolean
  isEmailChannel: boolean
  reviewStatus: TicketReviewStatusResponse | null
  productReviewStatus: TicketProductReviewStatusResponse | null
  hasOtherProductRequests: boolean
  canPostProductReview: boolean
  reviewBusy: boolean
  productReviewBusy: boolean
  viewers: TicketViewerRow[]
  customer: CustomerRow | null
  avatarSrc?: string | null
  customerName: string
  sidebarEmail: string
  identifiers: DisplayIdent[]
  noteEditing: boolean
  noteDraft: string
  noteSaving: boolean
  sdAdminActions: SdAdminActionItem[]
  sdAdminBusyAction: TicketSdAdminApiAction | null
  sdAdminResult: TicketSdAdminApiActionResponse | null
  sdAdminEmailChoice: SdAdminEmailChoice | null
  sdAdminSelectedEmail: string | null
  customerFields: ProjectCustomerFieldRow[]
  previousTickets: TicketListItem[]
  displayedPreviousTickets: TicketListItem[]
  previousTicketsProjectCounts: Array<[string, number]>
  previousTicketsPreviewLimit: number
  projectPick: PreviousTicketsRowsListProps['projectPick']
  previousTicketTabStateById?: PreviousTicketsRowsListProps['tabStateById']
  onStatus: (status: string) => void
  onPriority: (priority: string) => void
  onAssign: (userId: string | null) => void
  onChangeReplyLocale: (locale: 'en' | 'tr' | null) => void
  onRequestReview: () => void
  onRequestProductReview: () => void
  onToggleProductReviewReceived: (received: boolean) => void
  onOpenAvatar: () => void
  onOpenIdentifiers: () => void
  onCopy: (value: string) => void
  onChangeNoteDraft: (value: string) => void
  onSetNoteEditing: (editing: boolean) => void
  onSaveNote: () => void
  onSelectedSdAdminEmailChange: (email: string | null) => void
  onCloseSdAdminEmailChoice: () => void
  onRunSdAdminAction: (action: TicketSdAdminApiAction, email?: string, sendToCustomer?: boolean) => void
  onUseSdAdminText: (text: string) => void
  onSelectPreviousTicket: PreviousTicketsRowsListProps['onSelectTicket']
  onOpenPreviousTicketInNewTab: PreviousTicketsRowsListProps['onOpenInNewTab']
  onShowAllPreviousTickets: () => void
  onOpenAttachments: () => void
  canViewKnowledgeBase?: boolean
  onOpenKnowledgeBase?: () => void
  onOpenSimilarClosedTickets?: () => void
}

type SidebarCollapsedBlocks = {
  customer: boolean
  knowledge: boolean
  previousTickets: boolean
}

const SIDEBAR_COLLAPSED_BLOCKS_KEY = 'ticketSidebarCollapsedBlocks:v1'

function readSidebarCollapsedBlocks(): SidebarCollapsedBlocks {
  try {
    const raw = localStorage.getItem(SIDEBAR_COLLAPSED_BLOCKS_KEY)
    if (!raw) throw new Error('empty')
    const parsed = JSON.parse(raw) as Partial<SidebarCollapsedBlocks>
    return {
      customer: !!parsed.customer,
      knowledge: !!parsed.knowledge,
      previousTickets: !!parsed.previousTickets,
    }
  } catch {
    return {
      customer: false,
      knowledge: false,
      previousTickets: false,
    }
  }
}

function SidebarCollapsibleCard({
  title,
  summary,
  collapsed,
  onToggle,
  children,
}: {
  title: string
  summary?: string
  collapsed: boolean
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <Card withBorder padding="sm" radius="md">
      <Group justify="space-between" gap="xs" wrap="nowrap" mb={collapsed ? 0 : 6}>
        <Box style={{ minWidth: 0 }}>
          <Text fw={600} size="xs">
            {title}
          </Text>
          {collapsed && summary ? (
            <Text size="xs" c="dimmed" truncate>
              {summary}
            </Text>
          ) : null}
        </Box>
        <Tooltip label={collapsed ? 'Развернуть' : 'Свернуть'} withArrow>
          <ActionIcon size="sm" variant="subtle" onClick={onToggle} aria-label={`Свернуть блок ${title}`}>
            {collapsed ? <IconChevronDown size={14} stroke={1.5} /> : <IconChevronUp size={14} stroke={1.5} />}
          </ActionIcon>
        </Tooltip>
      </Group>
      <Collapse in={!collapsed}>{children}</Collapse>
    </Card>
  )
}

export function TicketSidebarPanel({
  ticket,
  currentUser,
  isPhoneLayout,
  isTicketWideLayout,
  classicBadgeDark,
  statusOptions,
  assigneeOptions,
  assigneeUserById,
  assigneeFilter,
  renderAssigneeOption,
  showMyWorkReview,
  showProductReview,
  isEmailChannel,
  reviewStatus,
  productReviewStatus,
  hasOtherProductRequests,
  canPostProductReview,
  reviewBusy,
  productReviewBusy,
  viewers,
  customer,
  avatarSrc,
  customerName,
  sidebarEmail,
  identifiers,
  noteEditing,
  noteDraft,
  noteSaving,
  sdAdminActions,
  sdAdminBusyAction,
  sdAdminResult,
  sdAdminEmailChoice,
  sdAdminSelectedEmail,
  customerFields,
  previousTickets,
  displayedPreviousTickets,
  previousTicketsProjectCounts,
  previousTicketsPreviewLimit,
  projectPick,
  previousTicketTabStateById,
  onStatus,
  onPriority,
  onAssign,
  onChangeReplyLocale,
  onRequestReview,
  onRequestProductReview,
  onToggleProductReviewReceived,
  onOpenAvatar,
  onOpenIdentifiers,
  onCopy,
  onChangeNoteDraft,
  onSetNoteEditing,
  onSaveNote,
  onSelectedSdAdminEmailChange,
  onCloseSdAdminEmailChoice,
  onRunSdAdminAction,
  onUseSdAdminText,
  onSelectPreviousTicket,
  onOpenPreviousTicketInNewTab,
  onShowAllPreviousTickets,
  onOpenAttachments,
  canViewKnowledgeBase,
  onOpenKnowledgeBase,
  onOpenSimilarClosedTickets,
}: TicketSidebarPanelProps) {
  const [collapsedBlocks, setCollapsedBlocks] = useState<SidebarCollapsedBlocks>(() => readSidebarCollapsedBlocks())

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_BLOCKS_KEY, JSON.stringify(collapsedBlocks))
    } catch {
      /* ignore */
    }
  }, [collapsedBlocks])

  const toggleBlock = (key: keyof SidebarCollapsedBlocks) => {
    setCollapsedBlocks((current) => ({
      ...current,
      [key]: !current[key],
    }))
  }

  return (
    <Box
      style={{
        flex: isTicketWideLayout ? '0 0 clamp(200px, 28vw, 320px)' : '0 0 auto',
        width: isTicketWideLayout ? 'clamp(200px, 28vw, 320px)' : '100%',
        minWidth: isTicketWideLayout ? 200 : 0,
        maxWidth: isTicketWideLayout ? 320 : '100%',
        minHeight: 0,
        height: isTicketWideLayout ? '100%' : undefined,
        maxHeight: isTicketWideLayout ? '100%' : isPhoneLayout ? 'none' : 'min(40vh, 360px)',
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        overflow: isPhoneLayout ? 'visible' : 'hidden',
      }}
    >
      <ScrollArea
        type="auto"
        offsetScrollbars={!isPhoneLayout}
        scrollbars={isPhoneLayout ? false : undefined}
        style={{
          flex: isPhoneLayout ? '0 0 auto' : 1,
          minHeight: isPhoneLayout ? undefined : 0,
        }}
        viewportProps={
          isPhoneLayout
            ? {
                style: {
                  overflow: 'visible',
                  maxHeight: 'none',
                  height: 'auto',
                },
              }
            : undefined
        }
      >
        <Stack gap="xs" pr={6} pb="xs">
          <TicketManagementCard
            ticket={ticket}
            currentUser={currentUser}
            classicBadgeDark={classicBadgeDark}
            statusOptions={statusOptions}
            assigneeOptions={assigneeOptions}
            assigneeUserById={assigneeUserById}
            assigneeFilter={assigneeFilter}
            renderAssigneeOption={renderAssigneeOption}
            onStatus={onStatus}
            onPriority={onPriority}
            onAssign={onAssign}
            onChangeReplyLocale={onChangeReplyLocale}
          />

          <TicketReviewsCard
            showMyWorkReview={showMyWorkReview}
            showProductReview={showProductReview}
            isEmailChannel={isEmailChannel}
            reviewStatus={reviewStatus}
            productReviewStatus={productReviewStatus}
            hasOtherProductRequests={hasOtherProductRequests}
            canPostProductReview={canPostProductReview}
            reviewBusy={reviewBusy}
            productReviewBusy={productReviewBusy}
            onRequestReview={onRequestReview}
            onRequestProductReview={onRequestProductReview}
            onToggleProductReviewReceived={onToggleProductReviewReceived}
          />

          <TicketViewersCard
            viewers={viewers}
            usersById={assigneeUserById}
            currentUserId={currentUser?.id ?? undefined}
          />

          <TicketCustomerInfoCard
            ticket={ticket}
            customer={customer}
            avatarSrc={avatarSrc}
            customerName={customerName}
            sidebarEmail={sidebarEmail}
            identifiers={identifiers}
            noteEditing={noteEditing}
            noteDraft={noteDraft}
            noteSaving={noteSaving}
            onOpenAvatar={onOpenAvatar}
            onOpenIdentifiers={onOpenIdentifiers}
            onCopy={onCopy}
            onChangeNoteDraft={onChangeNoteDraft}
            onSetNoteEditing={onSetNoteEditing}
            onSaveNote={onSaveNote}
            collapsed={collapsedBlocks.customer}
            onToggleCollapsed={() => toggleBlock('customer')}
          />

          <TicketSdAdminCard
            actions={sdAdminActions}
            busyAction={sdAdminBusyAction}
            result={sdAdminResult}
            emailChoice={sdAdminEmailChoice}
            selectedEmail={sdAdminSelectedEmail}
            onSelectedEmailChange={onSelectedSdAdminEmailChange}
            onCloseEmailChoice={onCloseSdAdminEmailChoice}
            onRunAction={onRunSdAdminAction}
            onCopy={onCopy}
            onUseText={onUseSdAdminText}
          />

          {canViewKnowledgeBase ? (
            <SidebarCollapsibleCard
              title="База знаний"
              summary="БЗ и похожие обращения"
              collapsed={collapsedBlocks.knowledge}
              onToggle={() => toggleBlock('knowledge')}
            >
              <Stack gap={6}>
                <Button
                  size="compact-xs"
                  variant="light"
                  fullWidth
                  leftSection={<IconBook size={14} stroke={1.5} />}
                  onClick={onOpenKnowledgeBase}
                >
                  База знаний
                </Button>
                <Button
                  size="compact-xs"
                  variant="light"
                  fullWidth
                  leftSection={<IconMessage size={14} stroke={1.5} />}
                  onClick={onOpenSimilarClosedTickets}
                >
                  Похожие обращения
                </Button>
              </Stack>
            </SidebarCollapsibleCard>
          ) : null}

          <TicketCustomerFieldsCard customer={customer} fields={customerFields} onCopy={onCopy} />

          <TicketPreviousTicketsCard
            customerId={ticket.customer_id}
            previousTickets={previousTickets}
            displayedTickets={displayedPreviousTickets}
            projectCounts={previousTicketsProjectCounts}
            previewLimit={previousTicketsPreviewLimit}
            projectPick={projectPick}
            onSelectTicket={onSelectPreviousTicket}
            onOpenInNewTab={onOpenPreviousTicketInNewTab}
            tabStateById={previousTicketTabStateById}
            onShowAll={onShowAllPreviousTickets}
            collapsed={collapsedBlocks.previousTickets}
            onToggleCollapsed={() => toggleBlock('previousTickets')}
          />

          {ticket.customer_id ? (
            <Card withBorder padding="sm" radius="md">
              <Button
                size="compact-xs"
                variant="light"
                fullWidth
                leftSection={<IconPaperclip size={14} stroke={1.5} />}
                onClick={onOpenAttachments}
              >
                Все вложения клиента
              </Button>
            </Card>
          ) : null}
        </Stack>
      </ScrollArea>
    </Box>
  )
}
